import { useRef, useState } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { useFirm } from '../lib/firm/FirmProvider';
import {
  clearFirmLogo,
  fileToLogoUpload,
  setFirmAccent,
  uploadFirmLogo,
  MAX_LOGO_BYTES,
} from '../lib/firm/firmApi';
import './advisor.css';
import './branding.css';

const ACCENT_PRESETS = ['#C49A5A', '#B08D57', '#2E5A88', '#3F6F52', '#7A3B52', '#5B4B8A'];

export function BrandingScreen() {
  const { api } = useAuth();
  const { firm, reload } = useFirm();
  const fileRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // A firm that can't manage branding shouldn't be here (the nav hides the link),
  // but a deep-link deserves an honest wall rather than a broken form.
  if (firm !== null && !firm.can_manage_branding) {
    return (
      <div className="la-rise">
        <header className="page__head">
          <p className="la-label">Branding</p>
          <h1 className="page__title">Your firm's brand</h1>
        </header>
        <p className="la-card privacyNote">
          Only your firm's primary administrator can change the logo and colour your clients see.
          Ask them to set it up, and it will appear here for everyone at {firm.name}.
        </p>
      </div>
    );
  }

  async function onPickFile(file: File): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const payload = await fileToLogoUpload(file);
      await uploadFirmLogo(api, payload);
      await reload();
      setNotice('Your logo is live. Your clients will see it too.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That upload did not go through.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function onClearLogo(): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await clearFirmLogo(api);
      await reload();
      setNotice('Logo removed. The portal is back to your firm name.');
    } catch {
      setError('We could not remove the logo just now.');
    } finally {
      setBusy(false);
    }
  }

  async function onAccent(accent: string | null): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await setFirmAccent(api, accent);
      await reload();
      setNotice(accent === null ? 'Accent reset to the default.' : 'Accent colour updated.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We could not save that colour.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="la-rise">
      <header className="page__head">
        <p className="la-label">Branding</p>
        <h1 className="page__title">Your firm's brand</h1>
        <p className="page__sub">
          Make this yours. Your logo and colour appear across your team's portal — and on the app
          your clients use, so the experience feels like it comes from {firm?.name ?? 'your firm'}.
        </p>
      </header>

      {error !== null && (
        <p className="auth__alert" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="brand__notice" role="status">
          {notice}
        </p>
      )}

      <div className="la-card formCard">
        <p className="la-label">Logo</p>
        <div className="brand__logoRow">
          <div className="brand__logoPreview" aria-hidden={firm?.logo_url === null}>
            {firm?.logo_url ? (
              <img src={firm.logo_url} alt={`${firm.name} logo`} />
            ) : (
              <span className="brand__logoPlaceholder">{firm?.name ?? 'Your firm'}</span>
            )}
          </div>
          <div className="brand__logoActions">
            <p className="brand__hint">PNG, JPEG, or WebP, up to {Math.round(MAX_LOGO_BYTES / 1024)} KB.
              A wide logo on a transparent background looks best on the dark portal.</p>
            <div className="brand__btnRow">
              <button
                type="button"
                className="la-btn-primary"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                {firm?.logo_url ? 'Replace logo' : 'Upload logo'}
              </button>
              {firm?.logo_url && (
                <button
                  type="button"
                  className="la-btn-secondary"
                  disabled={busy}
                  onClick={() => void onClearLogo()}
                >
                  Remove
                </button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="brand__fileInput"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPickFile(f);
              }}
            />
          </div>
        </div>
      </div>

      <div className="la-card formCard">
        <p className="la-label">Accent colour</p>
        <p className="brand__hint">
          Tints the highlights in the portal your team and clients see. Choose one that reads
          clearly on a dark background.
        </p>
        <div className="brand__swatches">
          {ACCENT_PRESETS.map((c) => (
            <button
              key={c}
              type="button"
              className={`brand__swatch${firm?.brand_accent?.toLowerCase() === c.toLowerCase() ? ' brand__swatch--active' : ''}`}
              style={{ background: c }}
              disabled={busy}
              aria-label={`Use ${c}`}
              aria-pressed={firm?.brand_accent?.toLowerCase() === c.toLowerCase()}
              onClick={() => void onAccent(c)}
            />
          ))}
          <label className="brand__customSwatch" title="Custom colour">
            <input
              type="color"
              value={firm?.brand_accent ?? '#C49A5A'}
              disabled={busy}
              onChange={(e) => void onAccent(e.target.value.toUpperCase())}
            />
            <span aria-hidden="true">+</span>
          </label>
        </div>
        {firm?.brand_accent && (
          <button
            type="button"
            className="la-btn-secondary brand__reset"
            disabled={busy}
            onClick={() => void onAccent(null)}
          >
            Reset to default gold
          </button>
        )}
      </div>

      <p className="la-card privacyNote">
        Branding is cosmetic. It never changes what your firm can see — you still only ever view
        metadata, never the contents of a client's messages.
      </p>
    </div>
  );
}
